import { useState, useEffect } from "react";
import { Briefcase, Home, Zap, Leaf, RefreshCw, Activity } from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT, useDeptName } from "@/contexts/LanguageContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from "recharts";

const COLORS_COM = "#244C36";
const COLORS_RES = "#3B82F6";
const PIE_COLORS = [COLORS_COM, COLORS_RES];

const CrossAuditReportView = ({ clientId }) => {
  const t = useT();
  const dn = useDeptName();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/clients/${clientId}/cross-audit/report`);
      setReport(response.data);
    } catch (error) {
      console.error("Error fetching cross-audit report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  const com = report?.commercial || {};
  const res = report?.residential || {};
  const totals = report?.totals || {};
  const insights = report?.insights || [];

  const energyPieData = [
    { name: t("commercial_audits"), value: com.total_energy_kwh || 0 },
    { name: t("residential_audits"), value: res.total_energy_kwh || 0 },
  ];
  const co2PieData = [
    { name: t("commercial_audits"), value: com.total_co2_kg || 0 },
    { name: t("residential_audits"), value: res.total_co2_kg || 0 },
  ];

  const allDepts = [
    ...(com.departments || []).map(d => ({ ...d, type: "commercial" })),
    ...(res.departments || []).map(d => ({ ...d, type: "residential" })),
  ];
  const barData = allDepts.map(d => ({
    name: d.name.length > 12 ? d.name.substring(0, 12) + ".." : d.name,
    energy: d.energy_kwh,
    co2: d.co2_kg,
    type: d.type,
  }));

  const renderCustomLabel = ({ name, percent }) => {
    if (percent === 0) return null;
    return `${name}: ${(percent * 100).toFixed(0)}%`;
  };

  return (
    <div data-testid="cross-audit-report-view">
      <div className="page-header flex-between">
        <div>
          <h2 className="page-title">Cross-Audit Report</h2>
          <p className="page-subtitle">Side-by-side comparison of Commercial vs Residential energy and emissions.</p>
          <p className="text-muted" style={{ fontSize: "var(--text-small)" }}>{t("generated")}: {report?.report_generated_at ? new Date(report.report_generated_at).toLocaleString() : "N/A"}</p>
        </div>
        <button className="btn btn-primary" onClick={fetchReport} data-testid="cross-audit-refresh">
          <RefreshCw size={16} /> {t("refresh")}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="stats-grid mb-lg">
        <div className="stat-card" data-testid="stat-grand-energy">
          <div className="stat-icon info"><Zap size={24} /></div>
          <div className="stat-value">{totals.grand_total_energy_kwh?.toLocaleString()}</div>
          <div className="stat-label">Total Energy (kWh)</div>
        </div>
        <div className="stat-card" data-testid="stat-grand-co2">
          <div className="stat-icon success"><Leaf size={24} /></div>
          <div className="stat-value">{totals.grand_total_co2_kg?.toLocaleString()}</div>
          <div className="stat-label">Total CO2e (kg)</div>
        </div>
        <div className="stat-card" data-testid="stat-grand-power">
          <div className="stat-icon primary"><Activity size={24} /></div>
          <div className="stat-value">{totals.grand_total_power_kw?.toLocaleString()}</div>
          <div className="stat-label">Total Power (kW)</div>
        </div>
      </div>

      {/* Commercial vs Residential Comparison Cards */}
      <div className="bento-grid mb-lg">
        <div className="card" style={{ borderTop: `4px solid ${COLORS_COM}` }}>
          <h4 className="card-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Briefcase size={18} /> {t("commercial_audits")}
          </h4>
          <div className="stats-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div><div className="stat-value" style={{ fontSize: "1.4rem" }}>{com.total_energy_kwh?.toLocaleString()}</div><div className="stat-label">Energy (kWh)</div></div>
            <div><div className="stat-value" style={{ fontSize: "1.4rem" }}>{com.total_co2_kg?.toLocaleString()}</div><div className="stat-label">CO2e (kg)</div></div>
            <div><div className="stat-value" style={{ fontSize: "1.4rem" }}>{com.applicable_departments}</div><div className="stat-label">Departments</div></div>
            <div><div className="stat-value" style={{ fontSize: "1.4rem" }}>{com.audits_completed}</div><div className="stat-label">Audits Done</div></div>
          </div>
        </div>
        <div className="card" style={{ borderTop: `4px solid ${COLORS_RES}` }}>
          <h4 className="card-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Home size={18} /> {t("residential_audits")}
          </h4>
          <div className="stats-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div><div className="stat-value" style={{ fontSize: "1.4rem" }}>{res.total_energy_kwh?.toLocaleString()}</div><div className="stat-label">Energy (kWh)</div></div>
            <div><div className="stat-value" style={{ fontSize: "1.4rem" }}>{res.total_co2_kg?.toLocaleString()}</div><div className="stat-label">CO2e (kg)</div></div>
            <div><div className="stat-value" style={{ fontSize: "1.4rem" }}>{res.applicable_departments}</div><div className="stat-label">Categories</div></div>
            <div><div className="stat-value" style={{ fontSize: "1.4rem" }}>{res.audits_completed}</div><div className="stat-label">Audits Done</div></div>
          </div>
        </div>
      </div>

      {/* Pie Charts */}
      <div className="bento-grid mb-lg">
        <div className="card">
          <h4 className="card-title">Energy Distribution</h4>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={energyPieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={renderCustomLabel} labelLine={false}>
                {energyPieData.map((_, idx) => <Cell key={idx} fill={PIE_COLORS[idx]} />)}
              </Pie>
              <Tooltip formatter={(val) => `${val.toLocaleString()} kWh`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <h4 className="card-title">CO2 Emissions Distribution</h4>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={co2PieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={renderCustomLabel} labelLine={false}>
                {co2PieData.map((_, idx) => <Cell key={idx} fill={PIE_COLORS[idx]} />)}
              </Pie>
              <Tooltip formatter={(val) => `${val.toLocaleString()} kg CO2`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Combined Bar Chart */}
      <div className="card mb-lg">
        <h4 className="card-title">Energy by Department/Category</h4>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={barData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
            <XAxis dataKey="name" tick={{ fill: "#526A5A", fontSize: 10 }} angle={-20} textAnchor="end" height={60} />
            <YAxis tick={{ fill: "#526A5A", fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="energy" name="Energy (kWh)" radius={[4, 4, 0, 0]}>
              {barData.map((entry, idx) => (
                <Cell key={idx} fill={entry.type === "commercial" ? COLORS_COM : COLORS_RES} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Detailed Tables */}
      <div className="bento-grid mb-lg">
        <div className="card">
          <h4 className="card-title"><Briefcase size={16} style={{ marginRight: "6px" }} /> Commercial Departments</h4>
          <div className="table-container">
            <table className="table" data-testid="cross-audit-com-table">
              <thead>
                <tr><th>Department</th><th>Energy (kWh)</th><th>CO2 (kg)</th><th>Audit %</th></tr>
              </thead>
              <tbody>
                {(com.departments || []).map((d, i) => (
                  <tr key={i}>
                    <td>{d.name}</td>
                    <td>{d.energy_kwh?.toLocaleString()}</td>
                    <td>{d.co2_kg?.toLocaleString()}</td>
                    <td>{d.audit_completion}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <h4 className="card-title"><Home size={16} style={{ marginRight: "6px" }} /> Residential Categories</h4>
          <div className="table-container">
            <table className="table" data-testid="cross-audit-res-table">
              <thead>
                <tr><th>Category</th><th>Energy (kWh)</th><th>CO2 (kg)</th><th>Audit %</th></tr>
              </thead>
              <tbody>
                {(res.departments || []).map((d, i) => (
                  <tr key={i}>
                    <td>{d.name}</td>
                    <td>{d.energy_kwh?.toLocaleString()}</td>
                    <td>{d.co2_kg?.toLocaleString()}</td>
                    <td>{d.audit_completion}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="card">
        <h4 className="card-title">Insights & Recommendations</h4>
        <ul style={{ paddingLeft: "var(--space-lg)" }}>
          {insights.map((insight, i) => (
            <li key={i} style={{ marginBottom: "var(--space-sm)" }}>{insight}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default CrossAuditReportView;
